import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useMarketplacePermissions } from '@anytag/auth';
import { SocialAccountStatus, ApplicantInfluencer, MarketplaceCampaignAppliedStatus } from '@anytag/graphql';
import { Badge, BadgeProps } from '@anyui/core';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { bigIntFormatter, formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { InfluencerAvatar, InfluencerWidgetInformation } from '@anytag/shared-molecules';
import { THEME } from '@anytag/shared-constants';
import {
  StatsSection,
  SocialMediasSection,
  ProfileWidgetTemplate,
  WidgetSelectButtonProps
} from '../../../Influencers';

interface ProfileWidgetProps {
  className?: string;
  profile: ApplicantInfluencer;
  selectButtonProps?: WidgetSelectButtonProps;
}

export const ProfileWidget = ({
  className,
  selectButtonProps,
  profile: {
    id,
    age,
    name,
    email,
    avatar,
    gender,
    status,
    country,
    twitter,
    tiktok,
    youtube,
    facebook,
    instagram,
    appliedStatus,
    followersCount,
    engagement: { averageEngagement, engagementViewsRate, engagementFollowersRate }
  }
}: ProfileWidgetProps) => {
  const { t } = useTranslation();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const { hideToggleApplicantFromProposalBtn } = useMarketplacePermissions();

  const stats = [
    { label: t('HeaderColumn.Follower'), value: bigIntFormatter(followersCount, 0) },
    { label: t('HeaderColumn.Engagement'), value: formatIntNumber(averageEngagement) },
    { label: t('HeaderColumn.Egg Rate by followers'), value: formatPercent(engagementFollowersRate) },
    { label: t('HeaderColumn.Egg Rate by views'), value: formatPercent(engagementViewsRate) }
  ];

  const getStatusProps = (status: MarketplaceCampaignAppliedStatus): BadgeProps => {
    switch (status) {
      case MarketplaceCampaignAppliedStatus.APPLYING:
        return { children: t('Option.Applied'), color: 'yellow' };
      case MarketplaceCampaignAppliedStatus.APPROVED:
        return { children: t('Option.Approved'), color: 'green' };
      case MarketplaceCampaignAppliedStatus.REJECTED:
        return { children: t('Option.Rejected'), color: 'red' };
      case MarketplaceCampaignAppliedStatus.REVIEWING:
        return { children: t('Option.Reviewing'), color: 'purple' };
      default:
        return { children: null, color: 'zinc' };
    }
  };

  return (
    <ProfileWidgetTemplate
      className={className}
      selectButtonProps={!hideToggleApplicantFromProposalBtn ? selectButtonProps : undefined}
    >
      <InfluencerAvatar size="80px" src={avatar} />

      <InfluencerWidgetInformation
        age={age}
        name={name}
        gender={gender}
        country={country}
        isSignedUp={status === SocialAccountStatus.SIGNED_UP}
        profileUrl={generateProfileLink(id)}
      />

      <div css={styles.statusWrapper}>
        <p css={{ fontSize: '10px', fontWeight: 600, color: THEME.text.colors.gray.lv3 }}>{t('HeaderColumn.Status')}</p>
        <Badge className="mt-1" {...getStatusProps(appliedStatus)} />
      </div>

      <StatsSection id={id} stats={stats} css={styles.statsSection} />

      <SocialMediasSection
        tiktok={tiktok?.isAnySignedUpAccount}
        twitter={twitter?.isAnySignedUpAccount}
        youtube={youtube?.isAnySignedUpAccount}
        facebook={facebook?.isAnySignedUpAccount}
        instagram={instagram?.isAnySignedUpAccount}
        email={email ? true : undefined}
      />
    </ProfileWidgetTemplate>
  );
};

const styles = {
  statsSection: css({ marginRight: '16px', '.value': { marginTop: '8px' } }),
  statusWrapper: css({
    height: '37px',
    display: 'flex',
    minWidth: '80px',
    padding: '0 24px',
    alignItems: 'center',
    boxSizing: 'border-box',
    flexDirection: 'column'
  })
};
